// src/components/ui/sheet.jsx
import * as React from "react";
import {
  Dialog as BaseDialog,
  DialogContent as BaseDialogContent,
  DialogHeader as BaseDialogHeader,
  DialogTitle as BaseDialogTitle,
  DialogDescription as BaseDialogDescription,
} from "components/ui/dialog";

/** Simple context so <SheetClose> can close the sheet */
const SheetCtx = React.createContext({ close: () => {} });

export function Sheet({ open, onOpenChange, children }) {
  const close = React.useCallback(() => onOpenChange?.(false), [onOpenChange]);
  return (
    <SheetCtx.Provider value={{ close }}>
      <BaseDialog open={open} onOpenChange={onOpenChange}>
        {children}
      </BaseDialog>
    </SheetCtx.Provider>
  );
}

/**
 * Drawer-like content. Uses position:fixed so it truly anchors to the edge
 * regardless of overlay padding/centering defaults from DialogContent.
 */
export function SheetContent({ side = "right", style, children, ...props }) {
  const isBottom = side === "bottom";

  const fixedBase = isBottom
    ? {
        // bottom sheet
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        height: "100dvh",
        width: "100vw",
        borderRadius: 0,
      }
    : {
        // right drawer
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: "min(920px, 96vw)",
        maxWidth: "96vw",
        height: "100dvh",
        borderRadius: 0,
        borderTop: 0,
        borderRight: 0,
        borderBottom: 0,
        animation: "slide-in-right var(--dur-slow) var(--ease-out)",
      };

  return (
    <BaseDialogContent
      {...props}
      style={{
        // override modal defaults so we don't get centered content
        margin: 0,
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...fixedBase,
        ...style,
      }}
    >
      {children}
    </BaseDialogContent>
  );
}

export const SheetHeader = BaseDialogHeader;
export const SheetTitle = BaseDialogTitle;
export const SheetDescription = BaseDialogDescription || (({ children }) => <p>{children}</p>);

/** Works like shadcn's <SheetClose asChild> */
export function SheetClose({ asChild, children, ...rest }) {
  const { close } = React.useContext(SheetCtx);

  if (asChild && React.isValidElement(children)) {
    const child = React.Children.only(children);
    return React.cloneElement(child, {
      ...rest,
      onClick: (e) => {
        child.props?.onClick?.(e);
        close();
      },
    });
  }

  return (
    <button
      type="button"
      {...rest}
      onClick={(e) => {
        rest?.onClick?.(e);
        close();
      }}
    >
      {children ?? "Close"}
    </button>
  );
}
